import { el } from './dom.js';
import { TRAINING } from '../core/SimConfig.js';

/**
 * Top-left objective board for training missions.
 *
 * Rows are pushed in as objectives become active and pulled out as they're
 * cleared: a new row slides in from the left as it fades up, and a completed
 * row ticks green, holds for a beat, then slides back out while its height
 * collapses so anything below it rises smoothly. Both are pure CSS transitions
 * (see `#objective-board` in _ui.scss) — this class only toggles classes.
 *
 * Rows carry an optional right-aligned detail slot, which waypoint objectives
 * use for a live range readout. That readout is the reason the board is kept
 * independent of the HUD visibility setting: with the spatial marker hidden it
 * is the only navigation cue left.
 */
export default class ObjectiveList {
  constructor({ title = '' } = {}) {
    this.el = el('div', 'objective-board');
    if (title) this.el.appendChild(el('div', 'objective-title', title));
    this._rows = el('div', 'objective-rows');
    this.el.appendChild(this._rows);
    document.getElementById('app').appendChild(this.el);

    /** @type {Map<string, {row: HTMLElement, detail: HTMLElement, timers: number[]}>} */
    this._entries = new Map();
  }

  /** Push a new objective row and animate it in. */
  add(id, text) {
    if (this._entries.has(id)) return;
    const row = el('div', 'objective-row');
    const marker = el('span', 'objective-marker', '▸');
    const label = el('span', 'objective-text', text);
    const detail = el('span', 'objective-detail');
    row.append(marker, label, detail);
    this._rows.appendChild(row);
    // Toggle on the next frame so the row transitions from its entry pose
    // instead of being painted in place.
    const raf = requestAnimationFrame(() => row.classList.add('is-visible'));
    this._entries.set(id, { row, marker, detail, timers: [], raf });
  }

  /** Set (or clear) the right-hand detail text — used for waypoint range. */
  setDetail(id, text) {
    const entry = this._entries.get(id);
    if (entry && entry.detail.textContent !== text) entry.detail.textContent = text;
  }

  /** Tick the row green, hold, then slide it away and drop it. */
  complete(id) {
    const entry = this._entries.get(id);
    if (!entry || entry.done) return;
    entry.done = true;
    entry.marker.textContent = '✓';
    entry.row.classList.add('is-complete');
    this._retire(entry, TRAINING.completeHold * 1000);
  }

  /** Slide a row away without the completion tick (mission torn down early). */
  remove(id) {
    const entry = this._entries.get(id);
    if (!entry || entry.done) return;
    entry.done = true;
    this._retire(entry, 0);
  }

  _retire(entry, holdMs) {
    entry.timers.push(setTimeout(() => {
      entry.row.classList.add('is-leaving');
      entry.timers.push(setTimeout(() => entry.row.remove(), 600));
    }, holdMs));
  }

  setVisible(v) { this.el.classList.toggle('is-hidden', !v); }

  dispose() {
    for (const entry of this._entries.values()) {
      cancelAnimationFrame(entry.raf);
      for (const t of entry.timers) clearTimeout(t);
    }
    this._entries.clear();
    this.el.remove();
  }
}
